import React, { useState } from 'react';
import useAsyncEffect from 'use-async-effect';
import moment from 'moment-timezone';
import ReactECharts from 'echarts-for-react';
import { FiSend, FiMail, FiTrendingUp, FiAlertCircle, FiExternalLink, FiUsers } from 'react-icons/fi';
import { FaLinkedin, FaTwitter, FaFacebook, FaInstagram, FaYoutube } from 'react-icons/fa';
import { CampaignStatisticsContact, CampaignStatisticsLink, CampaignStatisticsResult } from '../../../model/GetCampaignsResult';
import api from '../../../api/api';
import { pluralize, to } from '../../../util/util';
import { initials } from '../Header';

const TZ = 'Australia/Sydney';

const socials = [
    { host: /(^|\.)linkedin\.com$/, name: 'LinkedIn', icon: <FaLinkedin /> },
    { host: /(^|\.)(twitter|x)\.com$/, name: 'X', icon: <FaTwitter /> },
    { host: /(^|\.)facebook\.com$/, name: 'Facebook', icon: <FaFacebook /> },
    { host: /(^|\.)instagram\.com$/, name: 'Instagram', icon: <FaInstagram /> },
    { host: /(^|\.)(youtube\.com|youtu\.be)$/, name: 'YouTube', icon: <FaYoutube /> },
];

const urlOf = (url: string) => { try { return new URL(url); } catch { return null; } };

// social links read as the network (the email's text is often the bare url), the rest by their link text
const social = (url: string) => {
    const host = urlOf(url)?.hostname ?? '';
    return socials.find(x => x.host.test(host));
};

const linkText = (link: CampaignStatisticsLink) => social(link.url)?.name ?? link.text;

const clickedText = (x: CampaignStatisticsContact) => {
    if (x.clickedLinkCount > 1) return `clicked ${x.clickedLinkCount} links`;
    if (!x.clickedLink) return '';
    const isPdf = /\.pdf$/i.test(urlOf(x.clickedLink.url)?.pathname ?? '');
    return `clicked ${isPdf ? 'PDF' : linkText(x.clickedLink)}`;
};

const pct = (n: number) => `${n.toFixed(1)}%`;

// spec 2:60217. Omitted: unsubscriber name on the tile (the list below has them), industry benchmark, "7 days after sending", "Best time to send", Broker / Media / SMSF badges, Clean up Bounced Contacts, Duplicate, Export.
const CampaignStatistics: React.FC<{ campaignId: string }> = ({ campaignId }) => {
    const [s, setS] = useState<CampaignStatisticsResult | null>(null);
    const [error, setError] = useState('');

    useAsyncEffect(async isMounted => {
        const [err, data] = await to(api().getCampaignStatistics(campaignId));
        if (!isMounted()) return;
        if (err || !data) { setError(err?.message ?? 'Loading the statistics failed'); return; }
        setS(data);
    }, [campaignId]);

    if (error) return <div className="main campaign-statistics"><p className="error">{error}</p></div>;
    if (!s) return <div className="main campaign-statistics"><p className="loading">Loading statistics…</p></div>;

    const bounces = s.issues.filter(x => x.kind === 'bounce').length;
    const unsubscribes = s.issues.length - bounces;

    const stat = (icon: React.ReactNode, tone: string, label: string, value: number, sub: string) =>
        <div className="card stat">
            <div className="h"><span className={`icon-tile ${tone}`}>{icon}</span>{label}</div>
            <div className="v">{value.toLocaleString()}</div>
            <div className="s">{sub}</div>
        </div>;

    const sectionTitle = (icon: React.ReactNode, title: string, sub?: string) =>
        <div className="section-title">
            <span className="icon-tile">{icon}</span>
            <div><h2>{title}</h2>{sub && <p className="sub">{sub}</p>}</div>
        </div>;

    const funnel = [
        { key: 'clicked', label: 'Clicked', n: s.clicked },
        { key: 'opened', label: 'Opened', n: s.openedNoClick },
        { key: 'not-opened', label: 'Not opened', n: s.notOpened },
        { key: 'bounced', label: 'Bounced', n: s.bounced },
    ];
    const funnelTotal = funnel.reduce((a, x) => a + x.n, 0);

    const chart = {
        animation: false,
        grid: { left: 32, right: 24, top: 12, bottom: 24 },
        tooltip: {
            trigger: 'axis',
            backgroundColor: '#0b1739', borderWidth: 0, padding: [6, 10],
            textStyle: { color: '#fff', fontSize: 12 },
            formatter: (params: any[]) => `${params[0].name} · ${pluralize('open', params[0].value)}`,
        },
        xAxis: {
            type: 'category', boundaryGap: false, data: s.opensByHour.map(x => moment(x.startUtc).tz(TZ).format('ha')),
            axisLine: { show: false }, axisTick: { show: false }, axisLabel: { color: '#5f6b7d', fontSize: 11 },
        },
        yAxis: { type: 'value', splitLine: { lineStyle: { color: '#e5e9f0', type: 'dashed' } }, axisLabel: { color: '#5f6b7d', fontSize: 11 } },
        series: [{
            type: 'line', showSymbol: false, data: s.opensByHour.map(x => x.opens),
            lineStyle: { color: '#00B5D1', width: 2 }, itemStyle: { color: '#00B5D1' },
            areaStyle: { color: { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: 'rgba(0,181,209,0.3)' }, { offset: 1, color: 'rgba(0,181,209,0)' }] } },
        }],
    };

    return <div className="main campaign-statistics">
        <div className="stats">
            {stat(<FiSend />, 'green', 'Delivered', s.delivered, `${s.bounced} bounced (${pct(s.bouncedPct)})`)}
            {stat(<FiMail />, 'green', 'Opened', s.opened, pct(s.openedPct))}
            {stat(<FiTrendingUp />, 'cyan', 'Clicked', s.clicked, `${pct(s.clickedPct)} · ${pluralize('total click', s.totalClicks)}`)}
            {stat(<FiAlertCircle />, 'red', 'Unsubscribed', s.unsubscribed, pct(s.unsubscribedPct))}
        </div>

        <div className="columns">
            <div className="col">
                <div className="card">
                    {sectionTitle(<FiTrendingUp />, 'How Investors Engaged')}
                    <div className="funnel">
                        {funnel.map(x => x.n > 0 && <span key={x.key} className={x.key} style={{ flexGrow: x.n / funnelTotal }} />)}
                    </div>
                    <div className="legend">
                        {funnel.map(x => <span key={x.key}><i className={x.key} /> {x.label} {x.n}</span>)}
                    </div>
                    <h3>Opens by hour after sending</h3>
                    <ReactECharts option={chart} notMerge={true} style={{ height: 180 }} />
                </div>

                <div className="card">
                    {sectionTitle(<FiExternalLink />, 'Which Links Were Clicked')}
                    <div className="table-head"><span>Link</span><span className="num">Unique</span><span className="num">Total</span></div>
                    <ul className="scroll links">
                        {s.links.map(x => <li key={x.url}>
                            <a href={x.url} target="_blank" rel="noopener noreferrer" title={x.url}>{social(x.url)?.icon}{linkText(x)}</a>
                            <span className="num strong">{x.unique}</span>
                            <span className="num">{x.total}</span>
                        </li>)}
                        {s.links.length === 0 && <li className="none">No links clicked yet</li>}
                    </ul>
                    <p className="foot">{pluralize('link', s.links.length)} · {pluralize('click', s.totalClicks)} in total</p>
                </div>
            </div>

            <div className="col">
                <div className="card">
                    {sectionTitle(<FiUsers />, 'Most Engaged Contacts')}
                    <ul className="scroll people">
                        {s.engaged.map((x, i) => {
                            const clicked = clickedText(x);
                            return <li key={i}>
                                <span className="mark">{initials(x.name)}</span>
                                <div className="who">
                                    <div className="name">{x.name}</div>
                                    <div className="detail">Opened {x.opens}×{clicked && ` · ${clicked}`}</div>
                                </div>
                                {x.rank != null && <span className="badge" title="Rank on the share register">#{x.rank.toLocaleString()}</span>}
                            </li>;
                        })}
                        {s.engaged.length === 0 && <li className="none">Nobody has opened it yet</li>}
                    </ul>
                    <p className="foot">{s.opened.toLocaleString()} {s.opened === 1 ? 'person' : 'people'} opened it</p>
                </div>

                <div className="card">
                    {sectionTitle(<FiAlertCircle />, 'Bounces & Unsubscribes')}
                    <ul className="scroll issues">
                        {s.issues.map((x, i) => <li key={i}>
                            <i className={x.kind} />
                            <div className="who">
                                <div className="name">{x.email}</div>
                                <div className="detail">{x.kind === 'unsubscribe' ? `Unsubscribed ${moment(x.timeUtc).tz(TZ).format('D MMM, h:mm a')}` : x.detail}</div>
                            </div>
                        </li>)}
                        {s.issues.length === 0 && <li className="none">No bounces or unsubscribes</li>}
                    </ul>
                    <p className="foot">{pluralize('bounce', bounces)} and {pluralize('unsubscribe', unsubscribes)}</p>
                </div>
            </div>
        </div>
    </div>;
};

export default CampaignStatistics;
